import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable, Subscription} from 'rxjs';
import {IInstantChatSettings, ILocation} from '../models/ILocation';
import {environment} from '../../environments/environment';
import {ICheckinResponse} from '../models/ICheckinResponse';
import {ICheckin, ICheckinParticipants} from '../models/ICheckin';
import {ICheckinRequest} from '../models/ICheckinRequest';
import {INearbyRequest} from '../models/INearbyRequest';
import {ICheckinsRequest} from '../models/ICheckinsRequest';
import {each, find, flatten, keyBy} from 'lodash';
import {IUserData, MemberType} from '../models/IUserData';
import {IAppState} from '../models/IAppState';
import {Store} from '@ngrx/store';
import {IReservation} from '../models/IReservation';
import {PositionInList} from '../models/ICompanyCustomField';
import {FieldFormatPipe} from '../pipes/field-format.pipe';
import {Router} from '@angular/router';
import {TranslationService} from '../translation';
import {AlertController} from '@ionic/angular';
import {IObjectMap} from '../models/IObjectMap';

export interface IChatTemplate {
  id: string;
  title: string;
  text: string;
}

export interface IVisitorProfile {
  phone: string;
  language: string;
  guid: string;
  avatarUrl: string;
}

export interface ICompanyUser {
  id: string;
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  active: boolean;
  invitationAcceptedAt: string;
  groupId: string;
  groupName: string;
  isDefaultGroup: boolean;
  locationId: string;
  locationName: string;
}

export interface ICompanyUserDto {
  items: ICompanyUser[];
  isEmpty: boolean;
  isNotEmpty: boolean;
  currentPage: number;
  resultsPerPage: number;
  totalPages: number;
  totalResults: number;
}

export interface ISmsInviteSetting {
  defaultGuestLanguage: string;
  useSmsAlways: boolean;
  enabled?: boolean;
  waitGuestInMinutes: number;
  activeForHours: number;
  ignorePinOnFirstLoginForMinutes: number;
  pin?: string;
  userPreferredLanguage?: string;
  requiresPinCode?: boolean;
}

export interface IInstantLink {
  id: string;
  checkinId: string;
  visitor: string;
  defaultGuestLanguage: string;
  useSmsAlways: boolean;
  requiresPinCode: boolean;
  activeForHours: number;
  ignorePinOnFirstLoginForMinutes: number;
  waitGuestInMinutes: number;
  isActive: boolean;
  expireAt: string;
  createdAt: string;
  ignorePinTillFirstLogin: string;
  shortUrl: string;
  pin: string;
  loginCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class LocationService {
  public userData: IUserData;
  private principalLocations: ILocation[];
  private state$: Observable<IAppState> = this.store.select((state) => state.AppStateReducer);
  private stateSub: Subscription;

  constructor(
    private http: HttpClient,
    private store: Store<{AppStateReducer: IAppState}>,
    private fieldFormatPipe: FieldFormatPipe,
    private router: Router,
    private ts: TranslationService,
    private alertController: AlertController
  ) {
    this.stateSub = this.state$.subscribe((appState: IAppState) => this.userData = appState.userData);
  }


  public async checkLocationEventsSetting(locationId: string, event: string) {
    const isLocationUser = this.userData.memberType === MemberType.companyMember;

    // no location check for non location user
    if (!isLocationUser) {
      return true;
    }

    if (!this.principalLocations) {
      const locations = await this.getPrincipalLocations().toPromise();
      this.principalLocations = locations.items;
    }
    const location = find(this.principalLocations, {id: locationId});

    return location.notificationEvents.indexOf(event) > -1;
  }

  public getLocations(): Observable<ILocation[]> {

    return this.http.get<ILocation[]>(`${environment.apiUrl}/locations`);
  }

  public getPrincipalLocations(): Observable<any> {

    return this.http.get<ILocation[]>(`${environment.apiUrl}/me/locations`, {
      params: {
        resultsPerPage: 1000
      }
    });
  }

  public getVisitorProfile(phone: string): Observable<IVisitorProfile> {

    return this.http.get<IVisitorProfile>(`${environment.apiUrl}/visitors/${phone}`, {
      headers: {
        errors: 'silent'
      }
    });
  }

  public getLocationById(id: string, silent?: boolean): Observable<ILocation> {

    return this.http.get<ILocation>(`${environment.apiUrl}/nearby/${id}`, {
      headers: {
        errors: silent ? 'silent' : 'all'
      },
      params: {silent}
    });
  }

  public getLocationForReservationById(id: string, silent?: boolean): Observable<ILocation> {

    return this.http.get<ILocation>(`${environment.apiUrl}/nearby/${id}/reservation`, {
      headers: {
        errors: silent ? 'silent' : 'all'
      },
      params: {silent}
    });
  }


  public getCompanyMemberGroups(companyId: string, silent?: boolean): Observable<ICompanyUserDto> {
    return this.http.get<ICompanyUserDto>(`${environment.apiUrl}/companies/${companyId}/locations-groups-members`, {
      headers: {
        errors: silent ? 'silent' : 'all'
      },
      params: {
        companyId: companyId,
        silent: silent,
        page: 0,
        resultsPerPage: 9999
      }
    });
  }

  public getCheckinParticipants(id: string, silent?: boolean): Observable<ICheckinParticipants[]> {
    return this.http.get<ICheckinParticipants[]>(`${environment.apiUrl}/checkins/${id}/chat/participants`, {
      headers: {
        errors: silent ? 'silent' : 'all'
      },
      params: {
        silent: silent
      }
    });
  }

  public inviteCheckinParticipant(checkinId: string, participantIds: string | string[]): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}/checkins/${checkinId}/chat/participants`, flatten([
      participantIds
    ]));
  }

  public removeCheckinParticipant(checkinId: string, participantIds: string | string[]): Observable<any> {
    return this.http.delete<any>(`${environment.apiUrl}/checkins/${checkinId}/chat/participants`, {
      body: flatten([participantIds])
    });
  }

  public leaveChat(checkinId: string): Observable<any> {
    return this.http.delete<any>(`${environment.apiUrl}/checkins/${checkinId}/chat/participants`);
  }

  public getLocationNearby(request: INearbyRequest): Observable<ILocation[]> {

    return this.http.get<ILocation[]>(`${environment.apiUrl}/nearby`, {
      params: request as any
    });
  }

  public checkInLocations(): Observable<ILocation[]> {

    return this.http.get<ILocation[]>(`${environment.apiUrl}/locations`);
  }

  public checkOut(checkinId: string): Observable<ILocation[]> {

    return this.http.post<ILocation[]>(`${environment.apiUrl}/checkins/${checkinId}/checkout`, {checkinId});
  }

  public cancel(checkinId: string): Observable<ILocation[]> {
    return this.http.post<ILocation[]>(`${environment.apiUrl}/checkins/${checkinId}/cancel`, {checkinId});
  }

  public checkInPresence(checkinId: string): Observable<ILocation[]> {

    return this.http.post<ILocation[]>(`${environment.apiUrl}/checkins/${checkinId}/confirm-presence`, {checkinId});
  }

  public checkInDequeue(checkinId: string): Observable<ILocation[]> {

    return this.http.post<ILocation[]>(`${environment.apiUrl}/checkins/${checkinId}/confirm-dequeue`, {checkinId});
  }

  public setActive(checkinId: string): Observable<ILocation[]> {

    return this.http.post<ILocation[]>(`${environment.apiUrl}/checkins/${checkinId}/dequeue`, {checkinId});
  }

  public getCheckIns(request: ICheckinsRequest): Observable<ICheckinResponse> {
    return this.http.get<ICheckinResponse>(`${environment.apiUrl}/checkins`, {
      params: request as any
    });
  }

  public getCheckInById(id: string, silent?: boolean): Observable<ICheckin> {

    return this.http.get<ICheckin>(`${environment.apiUrl}/checkins/${id}`, {
      params: {
        checkinId: id,
        silent: silent
      },
      headers: {
        errors: silent ? 'silent' : 'all'
      }
    });
  }

  public postCheckIn(locationId: string, request: ICheckinRequest): Observable<string> {

    return this.http.post<string>(`${environment.apiUrl}/nearby/${locationId}`, request);
  }

  getChatTemplates(companyId: string, locationId: string): Observable<IChatTemplate[]> {
    const url = `${environment.apiUrl}/companies/${companyId}/locations/${locationId}/chat-message-templates`;

    return this.http.get<IChatTemplate[]>(url);
  }

  getFieldPosition(reservation: IReservation, pos: PositionInList): string {
    const fields = keyBy(reservation.location.fields, 'positionInList');
    const field = fields[pos];

    const fieldValues = {};
    each(reservation.fields, (val, key) => {
      fieldValues[key.toLowerCase()] = val;
    });
    if (field) {
      const val = fieldValues[field.id.toLowerCase()];
      const res = this.fieldFormatPipe.transform(val, field.id.toLowerCase(), reservation.location);

      return res;
    }

    return undefined;
  }

  public async checkInCancel(checkinId: string) {
    const alert = await this.alertController.create({
      header: this.ts.instant('Cancel Check-in'),
      message: this.ts.instant('Are you sure you want to cancel this checkin?'),
      buttons: [{
        text: this.ts.instant('No'),
        cssClass: 'secondary',
        role: 'cancel'
      }, {
        text: this.ts.instant('Yes'),
        handler: async () => {
          // TODO: where the redirection should be set here ?
          await this.cancel(checkinId).toPromise();
          this.router.navigate(['/search-location/'], {
            replaceUrl: true
          });
        }
      }]
    });
    await alert.present();
  }

  public getSmsInviteSettings(checkinId: string): Observable<ISmsInviteSetting> {
    return this.http.get<ISmsInviteSetting>(`${environment.apiUrl}/checkins/${checkinId}/chat/instant`);
  }

  public getInstantChatSettings(checkinId: string, userPhone: string): Observable<ISmsInviteSetting> {
    return this.http.get<ISmsInviteSetting>(`${environment.apiUrl}/checkins/${checkinId}/chat/instant`, {
      params: { user: userPhone }
    });
  }

  public sendChatMessageAsSms(checkinId: string, messageIds: string[]): Observable<ISmsInviteSetting> {
    return this.http.post<ISmsInviteSetting>(`${environment.apiUrl}/checkins/${checkinId}/chat/instant/sms`, {messageIds});
  }

  public getInstantLinks(checkinId: string): Observable<IInstantLink[]> {
    return this.http.get<IInstantLink[]>(`${environment.apiUrl}/checkins/${checkinId}/chat/instant/links`);
  }

  public getInstantCheckinLinks(checkinId: string): Observable<IInstantLink[]> {
    return this.http.get<IInstantLink[]>(`${environment.apiUrl}/checkins/${checkinId}/chat/instant/checkin-links`);
  }

  public extendChatInstantLink(checkinId: string, linkId: string): Observable<ISmsInviteSetting> {
    return this.http.put<ISmsInviteSetting>(`${environment.apiUrl}/checkins/${checkinId}/chat/instant/links/${linkId}/extend`, {});
  }

  public updateCheckinFields(checkinId: string, fields: IObjectMap<string>): Observable<ICheckin> {
    return this.http.put<ICheckin>(`${environment.apiUrl}/checkins/${checkinId}/fields`, {fields});
  }

  public setChatInstantLinkStatus(checkinId: string, linkId: string, status: boolean): Observable<boolean> {
    const params = new HttpParams()
      .set('status', status);
    return this.http.put<boolean>(`${environment.apiUrl}/checkins/${checkinId}/chat/instant/links/${linkId}/set-status`, {}, { params });
  }

  public resetChatInstantLinkPin(checkinId: string, linkId: string): Observable<boolean> {
    return this.http.put<boolean>(`${environment.apiUrl}/checkins/${checkinId}/chat/instant/links/${linkId}/reset-pin`, {});
  }

  public updateChatInstantSettings(checkinId: string, linkId: string, settings: IInstantChatSettings): Observable<ISmsInviteSetting> {
    return this.http.put<ISmsInviteSetting>(`${environment.apiUrl}/checkins/${checkinId}/chat/instant/${linkId}`, settings);
  }
}
